// src/admin/pages/AdminLogin.jsx
import React, { useMemo, useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { adminLogin } from "../../api/adminAuthApi";
import { setAdminSession, hasAdminSession } from "../adminAuth";

const BRAND = {
  pink: "#FF2D83",
  ink: "#0B0E13",
};

function IconMail({ className = "" }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" aria-hidden="true">
      <path
        d="M4 7.5A2.5 2.5 0 0 1 6.5 5h11A2.5 2.5 0 0 1 20 7.5v9A2.5 2.5 0 0 1 17.5 19h-11A2.5 2.5 0 0 1 4 16.5v-9Z"
        stroke="currentColor"
        strokeWidth="1.7"
      />
      <path
        d="M6.2 7.2 12 11.3l5.8-4.1"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IconLock({ className = "" }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" aria-hidden="true">
      <path
        d="M7.5 11V8.6A4.5 4.5 0 0 1 12 4.1a4.5 4.5 0 0 1 4.5 4.5V11"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
      />
      <path
        d="M6.6 11h10.8A2.6 2.6 0 0 1 20 13.6v4.8A2.6 2.6 0 0 1 17.4 21H6.6A2.6 2.6 0 0 1 4 18.4v-4.8A2.6 2.6 0 0 1 6.6 11Z"
        stroke="currentColor"
        strokeWidth="1.7"
      />
      <path
        d="M12 15.2v2.2"
        stroke="currentColor"
        strokeWidth="1.9"
        strokeLinecap="round"
      />
    </svg>
  );
}

function IconEye({ className = "" }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" aria-hidden="true">
      <path
        d="M2.8 12s3.5-6.5 9.2-6.5S21.2 12 21.2 12s-3.5 6.5-9.2 6.5S2.8 12 2.8 12Z"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinejoin="round"
      />
      <path
        d="M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z"
        stroke="currentColor"
        strokeWidth="1.7"
      />
    </svg>
  );
}

function IconEyeOff({ className = "" }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" aria-hidden="true">
      <path
        d="M3.2 5.1 20.8 18.9"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
      />
      <path
        d="M6 7.3C3.7 9.1 2.8 12 2.8 12s3.5 6.5 9.2 6.5c1.8 0 3.3-.4 4.6-1"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
      />
      <path
        d="M9.3 8.2A4.8 4.8 0 0 1 12 7.5c5.7 0 9.2 6.5 9.2 6.5s-1.1 2-3.2 3.6"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
      />
      <path
        d="M10 10.1A3 3 0 0 0 14 14"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
      />
    </svg>
  );
}

function IconArrowRight({ className = "" }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" aria-hidden="true">
      <path d="M5 12h12" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" />
      <path
        d="M13 7l5 5-5 5"
        stroke="currentColor"
        strokeWidth="1.9"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function AdminLoginBackground() {
  return (
    <div className="fixed inset-0 -z-10 overflow-hidden" aria-hidden="true">
      <div
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(1200px 700px at 15% 18%, rgba(255,45,131,0.16) 0%, rgba(255,45,131,0.05) 28%, rgba(11,14,19,0.0) 60%)," +
            "radial-gradient(1100px 700px at 85% 85%, rgba(148,163,184,0.10) 0%, rgba(11,14,19,0.0) 55%)," +
            `linear-gradient(135deg, ${BRAND.ink} 0%, #0A111A 42%, #0A0F16 100%)`,
        }}
      />

      <svg
        className="absolute inset-0 h-full w-full"
        viewBox="0 0 1440 900"
        preserveAspectRatio="xMidYMid slice"
      >
        <defs>
          <pattern id="grid" width="36" height="36" patternUnits="userSpaceOnUse">
            <path d="M 36 0 H 0 V 36" fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="1" />
          </pattern>

          <pattern id="dots" width="48" height="48" patternUnits="userSpaceOnUse">
            <circle cx="2" cy="2" r="1" fill="rgba(255,255,255,0.08)" />
          </pattern>

          <linearGradient id="panel" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor="rgba(255,255,255,0.08)" />
            <stop offset="1" stopColor="rgba(255,255,255,0.02)" />
          </linearGradient>

          <radialGradient id="pinkGlow" cx="25%" cy="18%" r="55%">
            <stop offset="0" stopColor="rgba(255,45,131,0.35)" />
            <stop offset="0.35" stopColor="rgba(255,45,131,0.14)" />
            <stop offset="1" stopColor="rgba(255,45,131,0)" />
          </radialGradient>

          <radialGradient id="steelGlow" cx="78%" cy="78%" r="60%">
            <stop offset="0" stopColor="rgba(148,163,184,0.22)" />
            <stop offset="0.5" stopColor="rgba(148,163,184,0.08)" />
            <stop offset="1" stopColor="rgba(148,163,184,0)" />
          </radialGradient>

          <linearGradient id="arc" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0" stopColor="rgba(255,255,255,0.00)" />
            <stop offset="0.35" stopColor="rgba(255,255,255,0.10)" />
            <stop offset="0.7" stopColor="rgba(255,45,131,0.18)" />
            <stop offset="1" stopColor="rgba(255,255,255,0.00)" />
          </linearGradient>

          <linearGradient id="fadeRight" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0" stopColor="rgba(255,255,255,1)" />
            <stop offset="0.62" stopColor="rgba(255,255,255,1)" />
            <stop offset="1" stopColor="rgba(255,255,255,0.18)" />
          </linearGradient>

          <mask id="rightClean">
            <rect x="0" y="0" width="1440" height="900" fill="url(#fadeRight)" />
          </mask>
        </defs>

        <g mask="url(#rightClean)">
          <rect x="0" y="0" width="1440" height="900" fill="url(#grid)" opacity="0.55" />
          <rect x="0" y="0" width="1440" height="900" fill="url(#dots)" opacity="0.55" />
        </g>

        <circle cx="320" cy="190" r="340" fill="url(#pinkGlow)" />
        <circle cx="1180" cy="760" r="420" fill="url(#steelGlow)" opacity="0.9" />

        <path
          d="M-120 140 C 220 20, 520 80, 720 0 L 720 220 C 520 300, 220 280, -120 360 Z"
          fill="url(#panel)"
          opacity="0.85"
        />
        <path
          d="M-160 520 C 180 430, 380 520, 640 430 L 740 900 L -160 900 Z"
          fill="rgba(255,255,255,0.04)"
        />

        <path
          d="M120 260 C 380 120, 600 140, 820 40"
          fill="none"
          stroke="url(#arc)"
          strokeWidth="2"
          strokeLinecap="round"
          opacity="0.8"
        />
        <path
          d="M140 640 C 420 500, 650 520, 900 420"
          fill="none"
          stroke="rgba(255,255,255,0.10)"
          strokeWidth="1.5"
          strokeLinecap="round"
          opacity="0.55"
        />

        <g opacity="0.55" mask="url(#rightClean)">
          <circle cx="210" cy="320" r="3" fill="rgba(255,255,255,0.22)" />
          <circle cx="310" cy="280" r="3" fill="rgba(255,45,131,0.24)" />
          <circle cx="430" cy="330" r="3" fill="rgba(255,255,255,0.18)" />
          <path d="M210 320 L310 280 L430 330" stroke="rgba(255,255,255,0.14)" strokeWidth="1.2" fill="none" />
          <circle cx="520" cy="180" r="2.5" fill="rgba(255,255,255,0.18)" />
          <circle cx="610" cy="150" r="2.5" fill="rgba(255,255,255,0.14)" />
          <path d="M520 180 L610 150" stroke="rgba(255,255,255,0.10)" strokeWidth="1.1" fill="none" />
        </g>

        <rect x="0" y="0" width="1440" height="900" fill="rgba(0,0,0,0.20)" opacity="0.25" />
      </svg>
    </div>
  );
}

export default function AdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const navigate = useNavigate();
  const location = useLocation();

  const next = new URLSearchParams(location.search).get("next") || "/admin";

  useEffect(() => {
    if (hasAdminSession()) {
      navigate("/admin", { replace: true });
    }
  }, [navigate]);

  const canSubmit = useMemo(() => {
    return email.trim().length > 3 && password.trim().length >= 3 && !busy;
  }, [email, password, busy]);

  async function submit(e) {
    e.preventDefault();
    if (!canSubmit) return;

    setErr("");
    setBusy(true);

    try {
      const res = await adminLogin(email.trim().toLowerCase(), password);
      setAdminSession(res?.token, res?.me, res?.expiresAtEpochMs);
      navigate(next, { replace: true });
    } catch (e2) {
      const msg =
        e2?.response?.data?.message ||
        e2?.message ||
        "Login failed. Please check your credentials.";
      setErr(String(msg));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen text-slate-100">
      <AdminLoginBackground />

      <div className="mx-auto flex w-full max-w-6xl items-center justify-between px-4 py-6 sm:px-6">
        <div className="flex items-center gap-4">
          <img
            src="/Wayroot (1600 x 500 px).svg"
            alt="Wayroot"
            className="h-12 w-auto select-none sm:h-14"
            draggable={false}
          />
        </div>

        <div
          className="inline-flex items-center gap-2 border border-white/15 bg-white/5 px-3 py-1.5 text-[11px] font-semibold text-white/80 backdrop-blur"
          style={{ borderRadius: 12 }}
        >
          <span
            className="h-1.5 w-1.5"
            style={{ background: BRAND.pink, borderRadius: 999 }}
          />
          Secure access
        </div>
      </div>

      <div className="mx-auto grid min-h-[calc(100vh-92px)] w-full max-w-6xl items-center gap-10 px-4 pb-10 sm:px-6 lg:grid-cols-2">
        <section className="hidden lg:block">
          <div className="max-w-xl">
            <div className="text-3xl font-extrabold tracking-tight text-white">
              Admin Console
            </div>

            <div className="mt-3 text-[15px] leading-relaxed text-white/70">
              Booking operations, partner management, and platform control.
            </div>

            <div className="mt-8 grid gap-3">
              <div
                className="border border-white/10 bg-white/5 px-5 py-4 text-sm text-white/80 backdrop-blur"
                style={{ borderRadius: 16 }}
              >
                <div className="font-semibold text-white">Privacy-first</div>
                <div className="mt-1 text-[13px] text-white/65">
                  Access restricted to authorized admins only.
                </div>
              </div>

              <div
                className="border border-white/10 bg-white/5 px-5 py-4 text-sm text-white/80 backdrop-blur"
                style={{ borderRadius: 16 }}
              >
                <div className="font-semibold text-white">Operational control</div>
                <div className="mt-1 text-[13px] text-white/65">
                  Manage bookings, owners, drivers, and escalations with clarity.
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="mx-auto w-full max-w-md lg:ml-auto lg:mr-0">
          <div className="mb-6 flex items-center justify-center lg:hidden">
            <img
              src="/Wayroot (1600 x 500 px).svg"
              alt="Wayroot"
              className="h-14 w-auto select-none"
              draggable={false}
            />
          </div>

          <form
            onSubmit={submit}
            className="border border-white/15 bg-white/10 shadow-[0_30px_120px_rgba(0,0,0,0.55)] backdrop-blur-xl"
            style={{ borderRadius: 18 }}
          >
            <div
              className="border-b border-white/10 px-6 py-5"
              style={{
                borderTopLeftRadius: 18,
                borderTopRightRadius: 18,
                background:
                  "linear-gradient(135deg, rgba(255,255,255,0.10) 0%, rgba(255,255,255,0.05) 60%, rgba(255,45,131,0.06) 100%)",
              }}
            >
              <div>
                <div className="text-sm font-extrabold text-white">Admin Sign in</div>
                <div className="mt-1 text-[13px] text-white/65">
                  Use your admin credentials to continue.
                </div>
              </div>
            </div>

            <div className="px-6 py-6">
              <div className="space-y-4">
                <div>
                  <label htmlFor="admin-email" className="text-xs font-semibold text-white/75">
                    Email
                  </label>
                  <div className="relative mt-1">
                    <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-white/55">
                      <IconMail className="h-4 w-4" />
                    </span>
                    <input
                      id="admin-email"
                      type="email"
                      autoComplete="username"
                      inputMode="email"
                      placeholder=""
                      className="w-full border border-white/15 bg-white/10 py-3 pl-10 pr-3 text-sm text-white outline-none"
                      style={{
                        borderRadius: 12,
                        boxShadow: "inset 0 1px 0 rgba(255,255,255,0.06)",
                        WebkitTextFillColor: "white",
                      }}
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      onFocus={(e) => {
                        e.currentTarget.style.boxShadow =
                          "0 0 0 3px rgba(255,45,131,0.20)";
                      }}
                      onBlur={(e) => {
                        e.currentTarget.style.boxShadow =
                          "inset 0 1px 0 rgba(255,255,255,0.06)";
                      }}
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="admin-password" className="text-xs font-semibold text-white/75">
                    Password
                  </label>
                  <div className="relative mt-1">
                    <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-white/55">
                      <IconLock className="h-4 w-4" />
                    </span>

                    <input
                      id="admin-password"
                      type={showPwd ? "text" : "password"}
                      autoComplete="current-password"
                      placeholder=""
                      className="w-full border border-white/15 bg-white/10 py-3 pl-10 pr-10 text-sm text-white outline-none"
                      style={{
                        borderRadius: 12,
                        boxShadow: "inset 0 1px 0 rgba(255,255,255,0.06)",
                        WebkitTextFillColor: "white",
                      }}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      onFocus={(e) => {
                        e.currentTarget.style.boxShadow =
                          "0 0 0 3px rgba(255,45,131,0.20)";
                      }}
                      onBlur={(e) => {
                        e.currentTarget.style.boxShadow =
                          "inset 0 1px 0 rgba(255,255,255,0.06)";
                      }}
                    />

                    <button
                      type="button"
                      onClick={() => setShowPwd((s) => !s)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 rounded-lg p-2 text-white/55 hover:text-white"
                      aria-label={showPwd ? "Hide password" : "Show password"}
                    >
                      {showPwd ? (
                        <IconEyeOff className="h-4 w-4" />
                      ) : (
                        <IconEye className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                </div>

                {err ? (
                  <div
                    className="border border-rose-300/20 bg-rose-500/10 px-4 py-3 text-sm text-rose-100"
                    style={{ borderRadius: 12 }}
                    role="alert"
                  >
                    {err}
                  </div>
                ) : null}

                <button
                  type="submit"
                  disabled={!canSubmit}
                  className="group flex w-full items-center justify-center gap-2 py-3 text-sm font-extrabold text-white transition disabled:cursor-not-allowed disabled:opacity-60"
                  style={{
                    background: BRAND.pink,
                    borderRadius: 12,
                    boxShadow: "0 14px 34px rgba(255,45,131,0.26)",
                  }}
                >
                  {busy ? "Signing in..." : "Sign in"}
                  <IconArrowRight className="h-4 w-4 opacity-90 transition group-hover:translate-x-0.5" />
                </button>

                <div className="flex items-center justify-between text-xs text-white/60">
                  <span>Need help?</span>
                  <a
                    href="mailto:support@wayroot.com"
                    className="font-semibold text-white/85 hover:text-white"
                  >
                    Contact support
                  </a>
                </div>
              </div>
            </div>

            <div className="border-t border-white/10 px-6 py-4 text-[11px] text-white/50">
              © {new Date().getFullYear()} Wayroot • Admin Console
            </div>
          </form>
        </section>
      </div>
    </div>
  );
}